import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Microsoft360ViewContext } from '../../../../shared/contexts/microsoft-360-view.context';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AccountType,
  CallingType,
  ClientErrorReason,
  ForwardingTargetType,
  ForwardingType,
  GroupLicenseAssignmentStatus,
  LicensingState,
  Microsoft360View,
  Microsoft365LicenseCount,
  Microsoft365LicensesFieldConfig,
  Microsoft365UserLicenses,
  Microsoft365UserLicenseStatus,
  MicrosoftSecurityGroupsFieldConfig,
  MicrosoftSharedVoicemailGroupsFieldConfig,
  MicrosoftTeamsDevices,
  PstnConnectivityType,
  ResourceAccount,
  State,
  StatusCategory,
  TileStatus,
  UnansweredDelayInSeconds,
  UnansweredTargetType,
  ZpmTileConfig,
} from '../../../../shared/models/generated/ziroModels';
import { Microsoft360ViewPollingService } from '../../../../shared/services/microsoft/microsoft-360-view-polling.service';
import { combineLatest, forkJoin, Observable, of, Subscription, switchMap } from 'rxjs';
import { MicrosoftTileMappingService } from '../microsoft-tile-mapping.service';
import { ServiceTileType } from '../../../cisco/user-detail-home/service-tile-type.model';
import { cloneDeep, isEqual } from 'lodash';
import { ToastService } from '../../../../shared/services/toast.service';
import { BottomNavClearButtonsList, BottomNavService } from '../../../../shared/bottom-nav/bottom-nav.service';
import { MicrosoftTile, MicrosoftTileState } from '../tiles.model';
import { MicrosoftDialPlansResource } from '../../../shared/resources/ms-dial-plans.resource';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';
import { TranslateService } from '@ngx-translate/core';
import { MicrosoftLicensesContext } from '../../../../shared/contexts/microsoft-licenses.context';
import { Nvp } from '../../../../shared/models/nvp';
import { MicrosoftTileConfigContext } from '../../../shared/contexts/microsoft-tile-config.context';
import { Microsoft365SecurityGroupsContext } from '../../../../shared/contexts/microsoft-365-security-groups.context';
import { MicrosoftSecurityGroupsMembershipContext } from '../../../../shared/contexts/microsoft-security-groups-membership.context';
import { MicrosoftSharedVoicemailGroupsMembershipContext } from '../../../../shared/contexts/microsoft-shared-voicemail-groups-membership.context';
import { DubberFieldConfigResource } from '../../../../shared/resources/dubber-field-config.resource';
import { BandwidthEmergencyCallingFieldConfigResource } from '../../../shared/resources/field-config/bandwidth-emergency-calling-field-config.resource';
import { BandwidthE911DlrsResource } from '../../../shared/resources/bandwidth-e911-dlrs.resource';
import { DubberService } from '../../../shared/services/dubber.service';
import { ButtonStyles } from '../../../../button/button.component';
import { ZiroModalService } from '../../../../shared/services/ziro-modal.service';
import { DeviceService } from '../../../shared/services/device.service';
import { SystemStatusContext } from '../../../../shared/contexts/system-status.context';
import { MicrosoftOnPremAdResource } from '../../../shared/resources/microsoft-on-prem-ad.resource';
import { MicrosoftEntraAttributesResource } from '../../../shared/resources/microsoft-entra-attributes.resource';
import { catchError, filter, map } from 'rxjs/operators';
import { DraasSharedExtensionsResource } from '../../../shared/resources/draas-shared-extensions.resource';
import { ZiroModalPromptModalOptions } from '../../../../modals/prompt-modal/prompt-modal.component';
import { MicrosoftVoicePoliciesResource } from '../../../shared/resources/microsoft-voice-policies.resource';
import { MicrosoftPhoneNumberAssignmentsResource } from '../../../shared/resources/microsoft-phone-number-assignments.resource';
import { TeamsCallQueueMembershipResource } from '../../../../shared/resources/teams-call-queue-membership.resource';
import { Microsoft365LicensesContext } from '../../../../shared/contexts/microsoft-365-licenses.context';
import {
  EditResourceAccountModalComponent,
  EditResourceAccountModalProperties,
} from '../edit-resource-account-modal/edit-resource-account-modal.component';
import { ZiroModalDetailedModalOptions } from '../../../../modals/detailed-modal/detailed-modal.component';
import { DelegateManagementResource } from '../../../shared/resources/delegate-management.resource';
import { TeamsCallHandlingAndForwardingResource } from '../../../shared/resources/teams-call-handling-and-forwarding.resource';
import { ToastTypes } from '../../../../shared/services/abstract/toast.service.abstract';
import { CallQueuesResource } from '../../../../shared/resources/call-queues.resource';
import { ResourceAccountsResource } from '../../../../resource-accounts/resource-accounts.resource';

interface Device extends MicrosoftTeamsDevices {
  tooltip: string;
  iconMarkup: string;
}

interface TileStates {
  resourceAccounts: MicrosoftTileState;
  ms365: MicrosoftTileState;
  msTeamsCalling: MicrosoftTileState;
  msTeamsAudioConferencing: MicrosoftTileState;
  dubber: MicrosoftTileState;
  msSecurityGroupMembership: MicrosoftTileState;
  sharedVoicemailGroups: MicrosoftTileState;
  callQueues: MicrosoftTileState;
  autoAttendants: MicrosoftTileState;
}

@Component({
  selector: 'ziro-microsoft-360-detail-home',
  templateUrl: './microsoft360-detail-home.component.html',
  providers: [
    MicrosoftTileMappingService,
    Microsoft360ViewPollingService,
    DubberFieldConfigResource,
    DubberService,
    DeviceService,
    MicrosoftSecurityGroupsMembershipContext,
    MicrosoftSharedVoicemailGroupsMembershipContext,
    DelegateManagementResource,
    TeamsCallHandlingAndForwardingResource,
  ],
  standalone: false,
})
export class Microsoft360DetailHomeComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private microsoft360ViewContext = inject(Microsoft360ViewContext);
  private microsoftLicenseContext = inject(MicrosoftLicensesContext);
  private microsoftTileMappingService = inject(MicrosoftTileMappingService);
  private microsoft360ViewPollingService = inject(Microsoft360ViewPollingService);
  private microsoft365LicensesContext = inject(Microsoft365LicensesContext);
  private microsoftPhoneNumberAssignmentsResource = inject(MicrosoftPhoneNumberAssignmentsResource);
  private microsoftVoicePoliciesResource = inject(MicrosoftVoicePoliciesResource);
  private teamsCallQueueResource = inject(TeamsCallQueueMembershipResource);
  private toastService = inject(ToastService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private bottomNavService = inject(BottomNavService);
  private bandwidthE911DlrsResource = inject(BandwidthE911DlrsResource);
  private bandwidthEmergencyCallingFieldConfigResource = inject(BandwidthEmergencyCallingFieldConfigResource);
  private microsoftDialPlansResource = inject(MicrosoftDialPlansResource);
  private translateService = inject(TranslateService);
  private microsoftTileConfigContext = inject(MicrosoftTileConfigContext);
  private _microsoft365SecurityGroupsContext = inject(Microsoft365SecurityGroupsContext);
  private _securityGroupsMembershipContext = inject(MicrosoftSecurityGroupsMembershipContext);
  private _sharedVoicemailGroupsMembershipContext = inject(MicrosoftSharedVoicemailGroupsMembershipContext);
  private _dubberService = inject(DubberService);
  private _callQueuesResource = inject(CallQueuesResource);
  private _resourceAccountsResource = inject(ResourceAccountsResource);
  private _ziroModalService = inject(ZiroModalService);
  private deviceService = inject(DeviceService);
  private _systemStatusContext = inject(SystemStatusContext);
  private _microsoftOnPremAdResource = inject(MicrosoftOnPremAdResource);
  private _microsoftEntraAttributesResource = inject(MicrosoftEntraAttributesResource);
  private draasSharedExtensionsResource = inject(DraasSharedExtensionsResource);
  private _delegateManagementResource = inject(DelegateManagementResource);
  private _teamsCallHandlingAndForwardingResource = inject(TeamsCallHandlingAndForwardingResource);

  TileStatus = TileStatus;
  ServiceTileType = ServiceTileType;
  ziroIcons = ZiroIcons;
  isLoading = true;
  userPrincipalName: string;
  isTeamsLicensingInProgress: boolean;
  isAudioLicensingInProgress: boolean;
  is365LicensesSyncInProgress: boolean;
  tile365LicensesSyncBadgeText = '';
  tileStates: TileStates = {
    resourceAccounts: null,
    ms365: null,
    msTeamsCalling: null,
    msTeamsAudioConferencing: null,
    dubber: null,
    msSecurityGroupMembership: null,
    sharedVoicemailGroups: null,
    callQueues: null,
    autoAttendants: null,
  };
  tileConfig: ZpmTileConfig;
  microsoft360View: Microsoft360View;
  isLicensesConfigured = false;
  readWriteLicenses: Microsoft365LicenseCount[] = [];
  isSecurityGroupsConfigured = false;
  isSharedVoicemailGroupsConfigured = false;
  hasAudioConferenceMismatch = false;
  devices: Device[] = [];

  private _subscriptions = new Subscription();
  private _securityGroupFieldConfig: MicrosoftSecurityGroupsFieldConfig;
  private _sharedVoicemailGroupsFieldConfig: MicrosoftSharedVoicemailGroupsFieldConfig;
  private _microsoftLicensesFieldConfig: Microsoft365LicensesFieldConfig;
  private initialDevices: MicrosoftTeamsDevices[];
  private _tenantId: string;
  private _resourceAccount: ResourceAccount;

  ngOnInit(): void {
    this.userPrincipalName = this.route.snapshot.paramMap.get('upn');
    this._tenantId = this.route.snapshot.params.tenantId;

    const msSub = this.microsoft360ViewContext.state$
      .pipe(
        filter((microsoft360View) => {
          this.microsoft360View = microsoft360View;

          if (this.microsoft360View.accountType === AccountType.SFB_ON_PREM_USER) {
            const options: ZiroModalPromptModalOptions = {
              windowClass: 'unsupported-account-type-modal',
              modalViewProperties: {
                title: this.translateService.instant('tkey;microsoft_360.view.unsupported_account_type_modal.title'),
                promptBody: this.translateService.instant(
                  'tkey;microsoft_360.view.unsupported_account_type_modal.body',
                ),
                icon: ZiroIcons.WARNING_TRIANGLE,
                buttons: [
                  {
                    label: 'tkey;microsoft_360.view.return_home.modal.button',
                    buttonClass: ButtonStyles.OUTLINE_PRIMARY,
                    dataAutomation: 'modal-confirm-button',
                    cb: () => of(this.router.navigate(['/'])),
                  },
                ],
              },
            };
            this._ziroModalService.openPromptModal(() => options.modalViewProperties, options);
            return false;
          } else if (this.microsoft360View.accountType === AccountType.UPN_NOT_IN_TEAMS) {
            const options: ZiroModalPromptModalOptions = {
              windowClass: 'account-not-in-teams-modal',
              modalViewProperties: {
                title: this.translateService.instant('tkey;microsoft_360.view.upn_not_in_teams_modal.title'),
                promptBody: this.translateService.instant('tkey;microsoft_360.view.upn_not_in_teams_modal.body', {
                  upn: this.userPrincipalName,
                }),
                icon: ZiroIcons.WARNING_TRIANGLE,
                buttons: [
                  {
                    label: 'tkey;microsoft_360.view.return_home.modal.button',
                    buttonClass: ButtonStyles.OUTLINE_PRIMARY,
                    dataAutomation: 'modal-confirm-button',
                    cb: () => of(this.router.navigate(['/'])),
                  },
                ],
              },
            };
            this._ziroModalService.openPromptModal(() => options.modalViewProperties, options);
            return false;
          }
          return true;
        }),
        switchMap(() =>
          combineLatest([
            this.microsoftLicenseContext.state$,
            this.microsoftTileConfigContext.state$,
            this._securityGroupsMembershipContext.state$,
            this._sharedVoicemailGroupsMembershipContext.state$,
          ]),
        ),
        switchMap(
          ([
            microsoftLicensesFieldConfig,
            microsoftTileConfig,
            securityGroupsFieldConfig,
            sharedVoicemailGroupsFieldConfig,
          ]) => {
            this.initialDevices = this.microsoft360View.teamsDevices;
            this.readWriteLicenses = microsoftLicensesFieldConfig.writeLicenseList;
            this._microsoftLicensesFieldConfig = microsoftLicensesFieldConfig;
            this.tileConfig = microsoftTileConfig;

            this.isLicensesConfigured =
              !!microsoftLicensesFieldConfig.writeLicenseList.length ||
              !!microsoftLicensesFieldConfig.readOnlyLicenseList.length;

            this.isSecurityGroupsConfigured =
              !!securityGroupsFieldConfig.readOnlySecurityGroupList.length ||
              !!securityGroupsFieldConfig.writeSecurityGroupList.length;
            this._securityGroupFieldConfig = securityGroupsFieldConfig;

            this.isSharedVoicemailGroupsConfigured =
              !!sharedVoicemailGroupsFieldConfig.readOnlyGroupList.length ||
              !!sharedVoicemailGroupsFieldConfig.writeGroupList.length;
            this._sharedVoicemailGroupsFieldConfig = sharedVoicemailGroupsFieldConfig;

            this._checkForLicenseStatus(this.microsoft360View);
            this._checkAudioConferenceLocationMismatch(this.microsoft360View);
            this._initTileStates(this.microsoft360View);

            if (
              this.tileConfig.resourceAccounts !== TileStatus.HIDE &&
              this.microsoft360View.resourceAccountDetails &&
              !this._resourceAccount
            ) {
              this._resourceAccountsResource
                .getResourceAccount(this._tenantId, this.userPrincipalName)
                .subscribe((resourceAccount) => (this._resourceAccount = resourceAccount));
            }

            if (
              this.isAudioLicensingInProgress ||
              this.isTeamsLicensingInProgress ||
              this.is365LicensesSyncInProgress
            ) {
              /** Poll every minute until licensed */
              this.microsoft360ViewPollingService.setBaseUrl(this.userPrincipalName, this._tenantId);
              this.microsoft360ViewPollingService.startPolling(Microsoft360ViewPollingService.MAX_POLLS);
              const pollingSub = this.microsoft360ViewPollingService.state$.subscribe((ms360View) => {
                if (!isEqual(ms360View, this.microsoft360View)) {
                  this.microsoft360View = ms360View;
                  /** set the new 360 view received from polling in context */
                  this.microsoft360ViewContext.setMicrosoft360View(ms360View);
                  this._checkForLicenseStatus(ms360View);
                }
              });
              this._subscriptions.add(pollingSub);
            }

            const devicesStatusObservables = this.initialDevices.map((device) =>
              this.deviceService.getDeviceStatus(this._tenantId, device.id),
            );
            return devicesStatusObservables.length ? forkJoin(devicesStatusObservables) : of(null);
          },
        ),
      )
      .subscribe((deviceStatuses) => {
        if (deviceStatuses?.length) {
          this.devices = this.initialDevices.map((device) => {
            const deviceStatus = deviceStatuses.find((deviceStatus) => deviceStatus.id === device.id);
            return {
              ...device,
              tooltip: this.getDeviceStatusTooltip(deviceStatus.healthStatus),
              iconMarkup: this.getDeviceStatusMarkup(deviceStatus.healthStatus),
            };
          });
        }
        this.isLoading = false;
      });

    this._subscriptions.add(msSub);
    this.bottomNavService.dispatch(new BottomNavClearButtonsList());
  }

  ngOnDestroy(): void {
    this.microsoft360ViewPollingService.stopPolling();
    this._subscriptions.unsubscribe();
  }

  getDeviceStatusTooltip(healthStatus: string): string {
    switch (healthStatus) {
      case 'healthy':
      case 'nonUrgent':
        return 'tkey;microsoft_360.view.teams.device.tooltip.healthy';
      case 'critical':
        return 'tkey;microsoft_360.view.teams.device.tooltip.critical';
      case 'offline':
        return 'tkey;microsoft_360.view.teams.device.tooltip.offline';
      default:
        return 'tkey;microsoft_360.view.teams.device.tooltip.unknown';
    }
  }

  getDeviceStatusMarkup(healthStatus: string): string {
    switch (healthStatus) {
      case 'healthy':
      case 'nonUrgent':
        return `<span class="fa-stack status-icon"><i class="${this.ziroIcons.CIRCLE} fa-stack-2x icon-success"></i><i class="${this.ziroIcons.SAVE_SUCCESS} fa-stack-1x"></i></span>`;
      case 'offline':
        return `<span class="fa-stack status-icon"><i class="fa ${this.ziroIcons.DESKPHONE_SMALL} fa-stack-1x"></i><i class="fa ${this.ziroIcons.DISABLED} fa-stack-2x icon-warning"></i></span>`;
      case 'critical':
      default:
        return `<i class="icon-warning-triangle icon-warning"></i>`;
    }
  }

  onAddTileClicked(tileState: MicrosoftTileState): void {
    switch (tileState.serviceType) {
      case MicrosoftTile.MS365:
        if (tileState.isEnabled) {
          this.router.navigate(['licenses'], { relativeTo: this.route });
        }
        break;
      case MicrosoftTile.MICROSOFT_TEAMS_CALLING:
        if (tileState.isEnabled) {
          this.router.navigate(['teams-calling'], { relativeTo: this.route });
        }
        break;
      case MicrosoftTile.MICROSOFT_TEAMS_AUDIO_CONFERENCING:
        if (tileState.isEnabled) {
          this.router.navigate(['audio-conference'], { relativeTo: this.route });
        }
        break;
      case MicrosoftTile.SECURITY_GROUP_MEMBERSHIP:
        if (tileState.isEnabled) {
          this.router.navigate(['security-group-membership'], { relativeTo: this.route });
        }
        break;
      case MicrosoftTile.SHARED_VOICEMAIL_GROUPS:
        if (tileState.isEnabled) {
          this.router.navigate(['shared-voicemail-groups'], { relativeTo: this.route });
        }
        break;
      case MicrosoftTile.DUBBER:
        if (tileState.isEnabled) {
          this.router.navigate(['dubber-compliance-recording'], { relativeTo: this.route });
        }
        break;
      case MicrosoftTile.CALL_QUEUES:
        if (tileState.isEnabled) {
          this.router.navigate(['teams-call-queue/new'], { relativeTo: this.route });
        }
        break;
      default:
        break;
    }
  }

  onEditTileClicked(tileState: MicrosoftTileState): void {
    if (!tileState.isEnabled) {
      return;
    }

    switch (tileState.serviceType) {
      case MicrosoftTile.MS365: {
        this.router.navigate(['licenses'], { relativeTo: this.route });
        break;
      }
      case MicrosoftTile.MICROSOFT_TEAMS_CALLING: {
        this.router.navigate(['teams-calling'], { relativeTo: this.route });
        break;
      }
      case MicrosoftTile.MICROSOFT_TEAMS_AUDIO_CONFERENCING: {
        this.router.navigate(['audio-conference'], { relativeTo: this.route });
        break;
      }
      case MicrosoftTile.SECURITY_GROUP_MEMBERSHIP: {
        this.router.navigate(['security-group-membership'], { relativeTo: this.route });
        break;
      }
      case MicrosoftTile.SHARED_VOICEMAIL_GROUPS: {
        this.router.navigate(['shared-voicemail-groups'], { relativeTo: this.route });
        break;
      }
      case MicrosoftTile.DUBBER: {
        this.router.navigate(['dubber-compliance-recording'], { relativeTo: this.route });
        break;
      }
      case MicrosoftTile.CALL_QUEUES:
        if (tileState.isEnabled) {
          this.router.navigate(
            [`teams-call-queue/${this.microsoft360View.resourceAccountDetails.associatedVoiceApplication.id}`],
            { relativeTo: this.route },
          );
        }
        break;
      case MicrosoftTile.AUTO_ATTENDANTS: {
        if (tileState.isEnabled) {
          this.router.navigate(
            [`teams-auto-attendant/${this.microsoft360View.resourceAccountDetails.associatedVoiceApplication.id}`],
            { relativeTo: this.route },
          );
        }
        break;
      }
      case MicrosoftTile.RESOURCE_ACCOUNTS: {
        const props: EditResourceAccountModalProperties = {
          upn: this.userPrincipalName,
          displayName: this._resourceAccount.displayName,
          resourceAccountType: this._resourceAccount.resourceAccountType,
          associatedVoiceApplication: this.microsoft360View.resourceAccountDetails?.associatedVoiceApplication ?? null,
          tenantId: this._tenantId,
        };
        const options: ZiroModalDetailedModalOptions = {
          size: 'lg',
          windowClass: 'edit-resource-account-modal',
          modalViewProperties: {
            title: this.translateService.instant('tkey;microsoft_360.resource_accounts.edit_modal.title'),
            ...props,
          },
          bodyClass: EditResourceAccountModalComponent,
        };
        this._ziroModalService
          .openDetailedModal(() => options.modalViewProperties, options)
          .subscribe((resourceAccount: ResourceAccount) => {
            if (!resourceAccount) {
              return;
            }
            this._resourceAccount = resourceAccount;
            this.microsoft360ViewContext._stateSource.next({
              ...this.microsoft360View,
              resourceAccountDetails: {
                ...this.microsoft360View.resourceAccountDetails,
                type: resourceAccount.resourceAccountType,
              },
            });
          });
        break;
      }
      default: {
        break;
      }
    }
  }

  onEditTileDeleteClicked(tileState: MicrosoftTileState): void {
    switch (tileState.serviceType) {
      case MicrosoftTile.MICROSOFT_TEAMS_AUDIO_CONFERENCING: {
        break;
      }
      default: {
        this.tileStates = {
          ...this.tileStates,
          [tileState.serviceType]: {
            ...this.tileStates[tileState.serviceType],
            tileType: ServiceTileType.DELETE,
          },
        };
        break;
      }
    }
  }

  onDeleteTileConfirmButtonClicked(tileState: MicrosoftTileState): void {
    this.tileStates = {
      ...this.tileStates,
      [tileState.serviceType]: {
        ...this.tileStates[tileState.serviceType],
        isDeleting: true,
      },
    };

    switch (tileState.serviceType) {
      case MicrosoftTile.MS365: {
        const microsoft365UserLicenses = this.microsoft360View.microsoft365UserLicenses;
        const licenseToUpdate = microsoft365UserLicenses.licenses.filter(
          (license) =>
            !this.readWriteLicenses.some(
              (readWriteLicense) => readWriteLicense.microsoft365LicenseJson.skuId === license.skuId,
            ),
        );
        const updatedMs365UserLicenses: Microsoft365UserLicenses = {
          ...microsoft365UserLicenses,
          licenses: licenseToUpdate,
        };

        const microsoft360ViewUpdated = cloneDeep(this.microsoft360View);
        microsoft360ViewUpdated.microsoft365UserLicenses = updatedMs365UserLicenses;

        this.microsoft365LicensesContext
          .updateUserLicenses(this._tenantId, microsoft365UserLicenses.userPrincipalName, updatedMs365UserLicenses)
          .subscribe(() => {
            microsoft360ViewUpdated.licensingStatus.teamsPhoneStatus = LicensingState.UNLICENSED;
            microsoft360ViewUpdated.licensingStatus.teamsAudioConferencingStatus = LicensingState.UNLICENSED;
            this._removeServiceFromMs360View(tileState.serviceType, microsoft360ViewUpdated);
            this._showDeleteToastForTile(tileState);
          });
        break;
      }
      case MicrosoftTile.MICROSOFT_TEAMS_CALLING: {
        this.microsoftDialPlansResource
          .getAllDialPlanGroups(this._tenantId)
          .pipe(
            switchMap((dialPlans) => {
              if (!dialPlans.length) {
                return of(null);
              }
              // find the dial plan field config applicable to this line
              if (this.microsoft360View.teamsPhoneNumberAssignment) {
                return this.microsoftDialPlansResource
                  .getDialPlanInventoriesByLineUri(
                    this._tenantId,
                    this.microsoft360View.teamsPhoneNumberAssignment.lineUri,
                    this.microsoft360View.teamsPhoneNumberAssignment.lineUriExtension,
                  )
                  .pipe(
                    map((dialPlansByUri) => {
                      if (!dialPlansByUri.length) {
                        return null;
                      }
                      return dialPlans.find(
                        (msDialPlanFieldConfig) => msDialPlanFieldConfig.id === dialPlansByUri[0].id,
                      );
                    }),
                  );
              } else {
                return of(
                  dialPlans.find(
                    (msDialPlanFieldConfig) =>
                      msDialPlanFieldConfig.mainNumber ===
                      this.microsoft360View.teamsVoicePolicies.sharedCallRoutingPolicy.resourceAccountLineUri,
                  ),
                );
              }
            }),
            switchMap((activeFieldConfig) => {
              if (!activeFieldConfig) {
                return of(null);
              }
              const deletionObs: Observable<void>[] = [of(null)];

              if (activeFieldConfig.onPremAdWriteDownEnabled) {
                deletionObs.push(this._deleteOnPremAdAttributes(activeFieldConfig.id));
              }

              if (activeFieldConfig.entraAttributeWriteDownEnabled) {
                deletionObs.push(this._deleteEntraAttributes(activeFieldConfig.id));
              }

              if (
                activeFieldConfig.callingType === CallingType.EXTENSION &&
                [PstnConnectivityType.ZIRO_DRAAS, PstnConnectivityType.ZIRO_DRAAS_BYOC].includes(
                  activeFieldConfig.pstnConnectivityType,
                )
              ) {
                // delete ziro sbc number configuration if it exists
                const lineUri = this.microsoft360View.teamsPhoneNumberAssignment.lineUri;
                const lineUriExtension = this.microsoft360View.teamsPhoneNumberAssignment.lineUriExtension;
                deletionObs.push(
                  this.draasSharedExtensionsResource.getExtension(this._tenantId, lineUri, lineUriExtension).pipe(
                    switchMap(() => {
                      return this.draasSharedExtensionsResource.deleteExtension(
                        this._tenantId,
                        lineUri,
                        lineUriExtension,
                      );
                    }),
                    catchError((err) => {
                      if (err.status === 404) {
                        return of(null);
                      }
                      throw err;
                    }),
                  ),
                );
              }

              if (activeFieldConfig.emergencyCallingSettingsConfigured) {
                // GET Bandwidth field-config and then DELETE
                deletionObs.push(
                  this.bandwidthEmergencyCallingFieldConfigResource
                    .get(
                      this._tenantId,
                      this.userPrincipalName,
                      this.microsoft360View.teamsPhoneNumberAssignment.lineUri,
                    )
                    .pipe(
                      switchMap((bandwidthFieldConfig) => {
                        return this.bandwidthE911DlrsResource.delete(
                          this._tenantId,
                          bandwidthFieldConfig.dynamicGeolocationEndpointId.value,
                        );
                      }),
                      catchError(() => {
                        // Do not throw an error here, our API returns a 500 if bandwidth settings do not exist
                        // so we just have to swallow all errors.
                        return of(null);
                      }),
                    ),
                );
              }

              return forkJoin(deletionObs);
            }),
            switchMap(() => {
              const auditTags: Nvp[] = [
                {
                  name: 'displayName',
                  value: this.microsoft360View.microsoft365UserLicenses.displayName,
                },
              ];
              return forkJoin([
                this.microsoft360View.teamsPhoneNumberAssignment
                  ? this.microsoftPhoneNumberAssignmentsResource.delete(
                      this.userPrincipalName,
                      this._tenantId,
                      auditTags,
                    )
                  : of(null),
                this.microsoftVoicePoliciesResource.delete(
                  this.userPrincipalName,
                  this.microsoft360View.accountType === AccountType.RESOURCE_ACCOUNT,
                  this._tenantId,
                ),
                this.teamsCallQueueResource.removeUserQueueMemberships(this.userPrincipalName, this._tenantId),
                this.microsoft360View.teamsPhoneNumberAssignment
                  ? this._teamsCallHandlingAndForwardingResource.putTeamsCallingHandlingForwarding(this._tenantId, {
                      userPrincipalName: this.userPrincipalName,
                      forwardingType: ForwardingType.NONE,
                      forwardingTargetType: ForwardingTargetType.NONE,
                      forwardingTarget: '',
                      unansweredTargetType: UnansweredTargetType.NONE,
                      unansweredTarget: '',
                      unansweredDelayInSeconds: UnansweredDelayInSeconds.NONE,
                    })
                  : of(null),
                this.microsoft360View.teamsPhoneNumberAssignment
                  ? this._delegateManagementResource.putCallingDelegate(this._tenantId, {
                      userPrincipalName: this.userPrincipalName,
                      assignedCallingDelegates: [],
                    })
                  : of(null),
              ]);
            }),
          )
          .subscribe({
            next: () => {
              const microsoft360ViewUpdated = this.microsoft360View;
              microsoft360ViewUpdated.teamsPhoneNumberAssignment = null;
              this._removeServiceFromMs360View(tileState.serviceType, microsoft360ViewUpdated);
              this._showDeleteToastForTile(tileState);
            },
            error: (response) => {
              if (
                response.status === 422 &&
                response.error.reason === ClientErrorReason.INSUFFICIENT_PERMISSION_TO_VIEW_OR_UPDATE_DIAL_PLAN_GROUP
              ) {
                const description = response.error.description.match(/\[(.*?)\]/);
                const options = {
                  windowClass: 'delete-button-modal',
                  modalViewProperties: {
                    icon: ZiroIcons.FORBIDDEN,
                    iconClass: 'text-danger',
                    modalBodyIconHeaderClass: 'animated bounceIn lead text-center text-danger',
                    title: this.translateService.instant(
                      'tkey;microsoft_360.view.teams.calling.permissions.modal.title',
                    ),
                    promptBody: this.translateService.instant(
                      'tkey;microsoft_360.view.teams.calling.permissions.modal.text',
                      { group: description[1] },
                    ),
                    displayCloseButton: false,
                    buttons: [
                      {
                        label: 'tkey;dialogs.button.ok',
                        buttonClass: ButtonStyles.OUTLINE_INFO,
                        dataAutomation: 'confirmation-modal-confirm-button',
                        cb: () => {
                          this.tileStates = {
                            ...this.tileStates,
                            [tileState.serviceType]: {
                              ...this.tileStates[tileState.serviceType],
                              tileType: ServiceTileType.EDIT,
                              isDeleting: false,
                            },
                          };
                          return of(null);
                        },
                      },
                    ],
                  },
                };

                this._ziroModalService.openPromptModal(() => options.modalViewProperties, options);
              } else {
                throw response;
              }
            },
          });
        break;
      }
      case MicrosoftTile.SECURITY_GROUP_MEMBERSHIP: {
        const securityGroupMemberships = this.microsoft360View.securityGroupMemberships;
        const groupsToKeep = securityGroupMemberships.filter((securityGroup) =>
          this._securityGroupFieldConfig.readOnlySecurityGroupList.some((readOnly) => readOnly.id === securityGroup.id),
        );
        this._microsoft365SecurityGroupsContext
          .get(this._tenantId, this.userPrincipalName)
          .subscribe((userGroupMemberships) => {
            userGroupMemberships.securityGroupMemberships = groupsToKeep.length ? groupsToKeep : [];
            const microsoft360ViewUpdated = this.microsoft360View;
            microsoft360ViewUpdated.securityGroupMemberships = groupsToKeep;

            this._microsoft365SecurityGroupsContext
              .updateUserGroups(this._tenantId, this.userPrincipalName, userGroupMemberships)
              .subscribe(() => {
                if (!groupsToKeep.length) {
                  this._removeServiceFromMs360View(tileState.serviceType, microsoft360ViewUpdated);
                } else {
                  this.microsoft360ViewContext._stateSource.next(microsoft360ViewUpdated);
                  this._initTileStates(microsoft360ViewUpdated);
                }
                this._showDeleteToastForTile(tileState);
              });
          });
        break;
      }
      case MicrosoftTile.DUBBER: {
        const auditTags: Nvp[] = [
          {
            name: 'upn',
            value: this.userPrincipalName,
          },
        ];
        this._dubberService
          .deleteDubberUser(
            this._tenantId,
            this.microsoft360View.dubberUser.region,
            this.microsoft360View.dubberUser.id,
            this.userPrincipalName,
            auditTags,
          )
          .subscribe(() => {
            const updated360View: Microsoft360View = {
              ...this.microsoft360View,
              dubberUser: null,
            };
            this.microsoft360ViewContext._stateSource.next(updated360View);
            this._showDeleteToastForTile(tileState);
          });
        break;
      }
      case MicrosoftTile.CALL_QUEUES: {
        this._callQueuesResource
          .delete(this._tenantId, this.microsoft360View.resourceAccountDetails.associatedVoiceApplication.id)
          .subscribe(() => {
            const queueName = this.microsoft360View.resourceAccountDetails.associatedVoiceApplication.displayName;
            this.toastService.pushDeleteToast('tkey;teams_call_queues.list_view.toast.service_type', queueName);
            const updated360View: Microsoft360View = {
              ...this.microsoft360View,
              resourceAccountDetails: {
                ...this.microsoft360View.resourceAccountDetails,
                associatedVoiceApplication: null,
              },
            };
            this.microsoft360ViewContext._stateSource.next(updated360View);
          });
        break;
      }
      case MicrosoftTile.RESOURCE_ACCOUNTS: {
        this._resourceAccountsResource.deleteResourceAccount(this._tenantId, this.userPrincipalName).subscribe(() => {
          this._showDeleteToastForTile(tileState);
          this.router.navigate(['/']);
        });
        break;
      }
      default: {
        break;
      }
    }
  }

  onDeleteTileCancelButtonClicked(tileState: MicrosoftTileState): void {
    switch (tileState.serviceType) {
      case MicrosoftTile.MICROSOFT_TEAMS_AUDIO_CONFERENCING: {
        break;
      }
      default: {
        this.tileStates = {
          ...this.tileStates,
          [tileState.serviceType]: {
            ...this.tileStates[tileState.serviceType],
            tileType: ServiceTileType.EDIT,
          },
        };
        break;
      }
    }
  }

  _initTileStates(microsoft360View: Microsoft360View): void {
    this.tileStates = {
      resourceAccounts: this.microsoftTileMappingService.mapServiceToTile(
        MicrosoftTile.RESOURCE_ACCOUNTS,
        this.tileConfig,
        microsoft360View,
        null,
        null,
        null,
      ),
      ms365: this.microsoftTileMappingService.mapServiceToTile(
        MicrosoftTile.MS365,
        this.tileConfig,
        microsoft360View,
        this.isLicensesConfigured,
        this.is365LicensesSyncInProgress,
        this._microsoftLicensesFieldConfig,
      ),
      msTeamsCalling: this.microsoftTileMappingService.mapServiceToTile(
        MicrosoftTile.MICROSOFT_TEAMS_CALLING,
        this.tileConfig,
        microsoft360View,
      ),
      msTeamsAudioConferencing: this.microsoftTileMappingService.mapServiceToTile(
        MicrosoftTile.MICROSOFT_TEAMS_AUDIO_CONFERENCING,
        this.tileConfig,
        microsoft360View,
      ),
      dubber: this.microsoftTileMappingService.mapServiceToTile(
        MicrosoftTile.DUBBER,
        this.tileConfig,
        microsoft360View,
      ),
      msSecurityGroupMembership: this.microsoftTileMappingService.mapServiceToTile(
        MicrosoftTile.SECURITY_GROUP_MEMBERSHIP,
        this.tileConfig,
        microsoft360View,
        this.isSecurityGroupsConfigured,
        null,
        this._securityGroupFieldConfig,
      ),
      sharedVoicemailGroups: this.microsoftTileMappingService.mapServiceToTile(
        MicrosoftTile.SHARED_VOICEMAIL_GROUPS,
        this.tileConfig,
        microsoft360View,
        this.isSharedVoicemailGroupsConfigured,
        null,
        this._sharedVoicemailGroupsFieldConfig,
      ),
      callQueues: this.microsoftTileMappingService.mapServiceToTile(
        MicrosoftTile.CALL_QUEUES,
        this.tileConfig,
        microsoft360View,
        null,
        null,
        null,
      ),
      autoAttendants: this.microsoftTileMappingService.mapServiceToTile(
        MicrosoftTile.AUTO_ATTENDANTS,
        this.tileConfig,
        microsoft360View,
        null,
        null,
        null,
      ),
    };
    this.changeDetectorRef.detectChanges();
  }

  private _checkForLicenseStatus(ms360View: Microsoft360View): void {
    this.isTeamsLicensingInProgress =
      ms360View.licensingStatus.teamsPhoneStatus === LicensingState.LICENSING_IN_PROGRESS;
    this.isAudioLicensingInProgress =
      ms360View.licensingStatus.teamsAudioConferencingStatus === LicensingState.LICENSING_IN_PROGRESS;
    if (
      ms360View.licensingStatus.groupLicenseAssignmentStatus.status ===
      Microsoft365UserLicenseStatus.LICENSING_IN_PROGRESS
    ) {
      this.is365LicensesSyncInProgress = true;
      this.tile365LicensesSyncBadgeText = 'tkey;userdetail.microsoft_365.licensing_in_progress.badge.text';
    } else if (
      ms360View.licensingStatus.groupLicenseAssignmentStatus.status ===
      Microsoft365UserLicenseStatus.UNLICENSING_IN_PROGRESS
    ) {
      this.is365LicensesSyncInProgress = true;
      this.tile365LicensesSyncBadgeText = 'tkey;userdetail.microsoft_365.unlicensing_in_progress.badge.text';
    } else {
      this.is365LicensesSyncInProgress = false;
      this.tile365LicensesSyncBadgeText = '';
      if (
        ms360View.licensingStatus.groupLicenseAssignmentStatus.status === Microsoft365UserLicenseStatus.LICENSING_ERROR
      ) {
        this._showLicenseAssignmentErrorModal(ms360View.licensingStatus.groupLicenseAssignmentStatus);
      }
    }

    const isAnyLicenseInProgress =
      this.isTeamsLicensingInProgress || this.isAudioLicensingInProgress || this.is365LicensesSyncInProgress;
    if (!isAnyLicenseInProgress) {
      this.microsoft360ViewPollingService.stopPolling();
    }
  }

  private _showLicenseAssignmentErrorModal(groupLicenseAssignmentStatus: GroupLicenseAssignmentStatus): void {
    const options: ZiroModalPromptModalOptions = {
      modalViewProperties: {
        title: this.translateService.instant('tkey;microsoft_360.view.license_assignment_error.title'),
        promptBody: this.translateService.instant('tkey;microsoft_360.view.license_assignment_error.body', {
          upn: this.userPrincipalName,
          license: groupLicenseAssignmentStatus.license,
          error: groupLicenseAssignmentStatus.errorReason,
        }),
        icon: ZiroIcons.CIRCLE_EXCLAMATION,
        iconClass: 'text-danger',
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;microsoft_360.view.license_assignment_error.continue_to_360_view',
            buttonClass: ButtonStyles.OUTLINE_PRIMARY,
            dataAutomation: 'continue-to-360-view-button',
            cb: () => of(true),
          },
        ],
      },
    };
    this._ziroModalService.openPromptModal(() => options.modalViewProperties, options);
  }

  private _removeServiceFromMs360View(serviceType: MicrosoftTile, ref: Microsoft360View = null): void {
    const microsoft360View = cloneDeep(ref);
    switch (serviceType) {
      case MicrosoftTile.MS365: {
        microsoft360View.teamsAudioConferencing = null;
        break;
      }
      case MicrosoftTile.MICROSOFT_TEAMS_CALLING: {
        microsoft360View.teamsPhoneNumberAssignment = null;
        microsoft360View.teamsVoicePolicies.sharedCallRoutingPolicy = {
          policyName: 'Global',
          resourceAccountUpn: '',
          resourceAccountLineUri: '',
          resourceAccountDisplayName: '',
        };
        break;
      }
      default:
        break;
    }
    this.microsoft360ViewContext._stateSource.next(microsoft360View);
    this._initTileStates(microsoft360View);
  }

  private _showDeleteToastForTile(tileState: MicrosoftTileState, ...args: any[]): void {
    switch (tileState.serviceType) {
      case MicrosoftTile.MS365: {
        this.toastService.pushDeleteToast('tkey;shared.model.microsoft_365.text', tileState.description);
        break;
      }
      case MicrosoftTile.MICROSOFT_TEAMS_CALLING: {
        this.toastService.push(
          ToastTypes.INFO,
          `${ZiroIcons.DELETE_SMALL} text-danger`,
          'tkey;shared.toast.delete.success.title',
          `${this.translateService.instant('tkey;microsoft_360.view.teams.calling.toast.text')} - <strong>${
            this.userPrincipalName
          } | ${tileState.description}
            </strong>`,
        );
        break;
      }
      case MicrosoftTile.SECURITY_GROUP_MEMBERSHIP: {
        this.toastService.pushDeleteToast('tkey;microsoft_360.view.security.groups.tile.title', tileState.description);
        break;
      }
      case MicrosoftTile.DUBBER: {
        this.toastService.pushDeleteToast(
          'tkey;microsoft_360.view.dubber.delete.toast.message',
          this.userPrincipalName,
        );
        break;
      }
      case MicrosoftTile.CALL_QUEUES: {
        this.toastService.pushDeleteToast('tkey;teams_call_queues.list_view.toast.service_type', args[0]);
        break;
      }
      case MicrosoftTile.RESOURCE_ACCOUNTS: {
        this.toastService.pushDeleteToast('tkey;microsoft_360.resource_accounts.tile.title', this.userPrincipalName);
        break;
      }
      default:
        break;
    }
  }

  private _deleteOnPremAdAttributes(dialPlanId: number): Observable<void> {
    return this._systemStatusContext.getSystemStatus().pipe(
      switchMap((systemStatus) => {
        const syncStatus = systemStatus.healthStatuses.find(
          (status) => status.category === StatusCategory.PROXY_SERVER,
        );
        const isZpcSyncWarningPresent = syncStatus && syncStatus.state !== State.OK;
        if (!isZpcSyncWarningPresent && !!this.microsoft360View.onPremiseUserPrincipalName) {
          return this._microsoftOnPremAdResource.getAdAttributes(
            this._tenantId,
            this.microsoft360View.onPremiseUserPrincipalName,
            dialPlanId,
          );
        } else {
          return of(null);
        }
      }),
      switchMap((adAttributes) => {
        if (adAttributes) {
          return this._microsoftOnPremAdResource.putAdAttributes(
            this._tenantId,
            this.microsoft360View.onPremiseUserPrincipalName,
            dialPlanId,
            {
              ...adAttributes,
              writeDownListJson: adAttributes.writeDownListJson.map((attr) => {
                return {
                  attributeName: attr.attributeName,
                  attributeValue: '',
                };
              }),
            },
          );
        } else {
          return of(null);
        }
      }),
    );
  }

  private _deleteEntraAttributes(dialPlanId: number): Observable<void> {
    if (this.microsoft360View.onPremiseUserPrincipalName) {
      return of(null);
    }
    return this._microsoftEntraAttributesResource
      .getEntraAttributes(this._tenantId, this.userPrincipalName, dialPlanId)
      .pipe(
        switchMap((entraAttributes) => {
          if (entraAttributes) {
            return this._microsoftEntraAttributesResource.putEntraAttributes(
              this._tenantId,
              this.userPrincipalName,
              dialPlanId,
              {
                ...entraAttributes,
                writeDownListJson: entraAttributes.writeDownListJson.map((attr) => {
                  return {
                    attributeName: attr.attributeName,
                    attributeValue: '',
                  };
                }),
              },
            );
          } else {
            return of(null);
          }
        }),
      );
  }

  private _checkAudioConferenceLocationMismatch(microsoft360View: Microsoft360View): void {
    const usageLocation = microsoft360View?.microsoft365UserLicenses?.usageLocation;
    const audioConferenceNumber = microsoft360View?.teamsAudioConferencing?.audioConferenceNumber;

    if (!usageLocation || !audioConferenceNumber) {
      this.hasAudioConferenceMismatch = false;
      return;
    }

    const match = audioConferenceNumber.match(/-\s*([^,]+)/);
    const audioCountry = match?.[1]?.trim();

    this.hasAudioConferenceMismatch = !!audioCountry && audioCountry.toLowerCase() !== usageLocation.toLowerCase();
  }
}
